import type { Color } from "@salestrak/ui";

/**
 * Widget data-source tags — the dashboard domain's vocabulary for "what a widget
 * hooks into." Rendered as gallery chips and folded into add-widget search so a
 * user can find every widget that touches a given entity (see `settings/add-widget-dialog`).
 *
 * Each concept carries its own deco `color`, so chips read as distinct colored
 * tags against the gallery entry's neutral background (a single neutral fill is invisible — the
 * dark-mode `secondary`/`card` tokens are identical). Chips render `variant="soft"`:
 * a subtle colored wash under vibrant colored text.
 *
 * The domain owns this enum; the domain-free `widget-kit` only carries the opaque
 * tag strings a definition declares (`WidgetCatalogEntry.tags`). Each definition's
 * `catalog` entry declares its tags with `satisfies readonly WidgetTag[]` so a
 * typo is a compile error — never magic strings.
 */
export const WIDGET_TAGS = {
  projects: { label: "Projects", color: "blue" },
  stages: { label: "Stages", color: "violet" },
  types: { label: "Types", color: "orange" },
  teams: { label: "Teams", color: "teal" },
  members: { label: "Members", color: "green" },
  workflows: { label: "Workflows", color: "purple" },
  ageHealth: { label: "Age health", color: "yellow" },
  activity: { label: "Activity", color: "cyan" },
  external: { label: "External", color: "pink" },
} as const satisfies Record<string, { label: string; color: Color }>;

export type WidgetTag = keyof typeof WIDGET_TAGS;

/** The exact deco colors used by tags — a narrow subset of `Color` (no `brand`). */
export type WidgetTagColor = (typeof WIDGET_TAGS)[WidgetTag]["color"];

/** Resolve a tag string (as carried opaquely on a preset) to its display label. */
export function widgetTagLabel(tag: string): string | undefined {
  return WIDGET_TAGS[tag as WidgetTag]?.label;
}

/** Resolve a tag string to its chip color (deco palette). */
export function widgetTagColor(tag: string): WidgetTagColor | undefined {
  return WIDGET_TAGS[tag as WidgetTag]?.color;
}
